<script setup lang="ts">
/**
 * Quiz/Ruler 3232:18886 — mobile measurement input. The scale slides under a fixed
 * centre indicator; the readout shows the value ("—" until the ruler is touched, so a
 * default is never saved by accident). Tapping the readout asks for the typed field.
 */
import { computed, ref } from 'vue'
import {
  FAIR_QUIZ_RULER_PX,
  fairQuizRulerDragValue,
  fairQuizRulerRest,
  fairQuizRulerTicks,
  snapFairQuizRuler,
  type FairQuizRulerOrientation,
} from './fairQuizRuler.data'

interface FairQuizRulerProps {
  modelValue: number | null
  min: number
  max: number
  step?: number
  unit: string
  orientation?: FairQuizRulerOrientation
  ariaLabel?: string
  /** Label of the readout button ("Type the value"). */
  typeLabel?: string
}

const props = withDefaults(defineProps<FairQuizRulerProps>(), {
  step: 1,
  orientation: 'horizontal',
  ariaLabel: undefined,
  typeLabel: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
  type: []
}>()

const isVertical = computed(() => props.orientation === 'vertical')
const px = computed(() => FAIR_QUIZ_RULER_PX[props.orientation])
const isSet = computed(() => props.modelValue !== null)
const position = computed(() => props.modelValue ?? fairQuizRulerRest(props.min, props.max, props.step))
const ticks = computed(() => fairQuizRulerTicks(props.min, props.max))

/** Offset of a tick from the indicator, px. */
function offset(value: number): string {
  return `calc(50% + ${(value - position.value) * px.value}px)`
}

const dragging = ref<{ id: number; start: number; from: number } | null>(null)

function axis(event: PointerEvent) {
  return isVertical.value ? event.clientY : event.clientX
}

function handlePointerDown(event: PointerEvent) {
  if (event.button !== 0) return
  dragging.value = { id: event.pointerId, start: axis(event), from: position.value }
  ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
}

function handlePointerMove(event: PointerEvent) {
  const drag = dragging.value
  if (!drag || drag.id !== event.pointerId) return
  const delta = axis(event) - drag.start
  // A tap is not a move: the ruler stays untouched until it actually slides.
  if (!isSet.value && Math.abs(delta) < 3) return
  emit('update:modelValue', fairQuizRulerDragValue(drag.from, delta, px.value, props.step, props.min, props.max))
}

function handlePointerUp() {
  dragging.value = null
}

function handleKeydown(event: KeyboardEvent) {
  const keys: Record<string, number> = {
    ArrowRight: 1,
    ArrowUp: isVertical.value ? -1 : 1,
    ArrowDown: isVertical.value ? 1 : -1,
    ArrowLeft: -1,
    PageUp: 5,
    PageDown: -5,
  }
  const direction = keys[event.key]
  if (direction === undefined) return
  event.preventDefault()
  emit(
    'update:modelValue',
    snapFairQuizRuler(position.value + direction * props.step, props.step, props.min, props.max),
  )
}

const shown = computed(() => (props.modelValue === null ? '—' : String(props.modelValue)))
</script>

<template>
  <div
    class="fair-quiz-ruler"
    :class="[
      `fair-quiz-ruler--${orientation}`,
      { 'fair-quiz-ruler--set': isSet, 'fair-quiz-ruler--dragging': dragging },
    ]"
  >
    <button
      type="button"
      class="fair-quiz-ruler__readout"
      :aria-label="typeLabel"
      @click="emit('type')"
    >
      <span class="fair-quiz-ruler__value">{{ shown }}</span>
      <span class="fair-quiz-ruler__unit">{{ unit }}</span>
    </button>

    <div
      class="fair-quiz-ruler__track"
      role="slider"
      tabindex="0"
      :aria-label="ariaLabel"
      :aria-valuemin="min"
      :aria-valuemax="max"
      :aria-valuenow="modelValue ?? undefined"
      :aria-valuetext="modelValue === null ? undefined : `${modelValue} ${unit}`"
      :aria-orientation="orientation"
      @pointerdown="handlePointerDown"
      @pointermove="handlePointerMove"
      @pointerup="handlePointerUp"
      @pointercancel="handlePointerUp"
      @keydown="handleKeydown"
    >
      <template
        v-for="tick in ticks"
        :key="tick.value"
      >
        <span
          class="fair-quiz-ruler__tick"
          :class="{ 'fair-quiz-ruler__tick--major': tick.major }"
          :style="isVertical ? { top: offset(tick.value) } : { left: offset(tick.value) }"
        />
        <span
          v-if="tick.major"
          class="fair-quiz-ruler__label"
          :style="isVertical ? { top: offset(tick.value) } : { left: offset(tick.value) }"
        >{{ tick.value }}</span>
      </template>
      <span class="fair-quiz-ruler__indicator" />
    </div>
  </div>
</template>
